<template>
  <div ref="root" class="supplier-phone-input" :class="{ 'has-error': error, 'has-status': mobileStatus }"
    @keydown.esc.stop.prevent="dismissTooltip">
    <input :id="id" type="tel" autocomplete="tel" :value="modelValue"
      :placeholder="placeholder" :required="required" :disabled="disabled" :readonly="readonly"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="showError ? errorTooltipId : undefined"
      @input="onInput" @focus="onFocus" />
    <button v-if="mobileStatus" ref="badge" type="button" class="mobile-access-badge"
      :aria-label="`Mobile access: ${mobileStatus}`"
      :aria-describedby="showInfo ? tooltipId : undefined"
      @mouseenter="badgeHovered = true; dismissed = false" @mouseleave="badgeHovered = false"
      @focus="badgeFocused = true; dismissed = false" @blur="badgeFocused = false"
      @click="badgeFocused = true; dismissed = false">
      <span class="mobile-access-badge-content" :class="{ 'is-on': mobileEnabled }">
        <AppointmentIcon name="mobile" :size="18" />
        <span>{{ mobileEnabled ? 'On' : 'Off' }}</span>
      </span>
    </button>
    <Teleport to="body">
      <div v-if="showInfo" :id="tooltipId" ref="tooltip" role="tooltip"
        class="supplier-phone-tooltip" :style="tooltipStyle">
        {{ mobileInfo }}
      </div>
      <div v-if="showError" :id="errorTooltipId" ref="errorTooltip" role="tooltip"
        class="supplier-phone-tooltip is-error" :class="{ 'is-above': errorTooltipAbove }" :style="errorTooltipStyle">
        {{ error }}
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { useFloatingPanel } from '@/composables/useFloatingPanel';

const props = defineProps({
  id: { type: String, required: true },
  modelValue: { type: String, default: '' },
  mobileStatus: { type: String, default: '' },
  error: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
});
const emit = defineEmits(['update:modelValue']);
const root = ref(null);
const badge = ref(null);
const tooltip = ref(null);
const errorTooltip = ref(null);
const active = ref(true);
const badgeHovered = ref(false);
const badgeFocused = ref(false);
const dismissed = ref(false);
const tooltipId = computed(() => `${props.id}-tooltip`);
const errorTooltipId = computed(() => `${props.id}-error-tooltip`);
const mobileEnabled = computed(() => props.mobileStatus.toLowerCase() === 'on');
const mobileInfo = computed(() => {
  if (props.mobileStatus === 'Turning on...') return 'Mobile access is being enabled. The status will update when saving finishes.';
  return mobileEnabled.value
    ? 'Mobile access is on. This supplier can use the mobile app.'
    : 'Mobile access is off. This supplier does not currently have access to the mobile app.';
});
const showInfo = computed(() => active.value && !dismissed.value && Boolean(props.mobileStatus) && (badgeHovered.value || badgeFocused.value));
const showError = computed(() => active.value && Boolean(props.error));
const tooltipStyle = useFloatingPanel({ open: showInfo, anchor: badge, panel: tooltip, width: 280 });
const errorPosition = useFloatingPanel({ open: showError, anchor: root, panel: errorTooltip, width: 'max-content', align: 'start' });
const errorTooltipAbove = computed(() => parseFloat(errorPosition.value.top) < (root.value?.getBoundingClientRect().top ?? 0));
const errorTooltipStyle = computed(() => {
  const left = parseFloat(errorPosition.value.left);
  const anchorLeft = root.value?.getBoundingClientRect().left ?? 0;
  return {
    ...errorPosition.value,
    visibility: Number.isFinite(left) ? 'visible' : 'hidden',
    '--tooltip-arrow-left': `${Math.max(12, anchorLeft + 18 - left)}px`,
  };
});

const dismissTooltip = () => { dismissed.value = true; };
const onFocus = () => {
  badgeFocused.value = false;
  badgeHovered.value = false;
  dismissed.value = false;
};
const onInput = (event) => {
  dismissed.value = false;
  emit('update:modelValue', event.target.value);
};
const onOutsidePointer = (event) => {
  if (!root.value?.contains(event.target)) {
    badgeFocused.value = false;
    badgeHovered.value = false;
    dismissTooltip();
  }
};
onMounted(() => document.addEventListener('pointerdown', onOutsidePointer));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutsidePointer));
onActivated(() => { active.value = true; });
onDeactivated(() => { active.value = false; dismissTooltip(); });
</script>

<style scoped>
.supplier-phone-input {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  height: 100%;
  background: var(--noc-background, #fff);
}

.supplier-phone-input input {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  align-self: stretch;
  padding: 10px 12px;
  border: 0 !important;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: var(--text-color);
  font: 400 14px/20px var(--font-family-base);
  box-sizing: border-box;
  cursor: text;
}

.supplier-phone-input.has-status input { padding-right: 88px; }
.supplier-phone-input:hover { background: var(--noc-background-secondary, #fafafb); }
.supplier-phone-input:focus-within { background: var(--noc-background, #fff); }
.supplier-phone-input input:focus { background: transparent; }
.supplier-phone-input.has-error::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 1px;
  background: var(--noc-red, #d9534f);
  pointer-events: none;
}

.mobile-access-badge {
  position: absolute;
  right: 8px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 2px;
  border: 0;
  background: transparent;
  cursor: pointer;
  touch-action: manipulation;
}

.mobile-access-badge-content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 5px;
  background: var(--noc-background-secondary, #fafafb);
  color: var(--text-color-secondary);
  font: 500 12px/18px var(--font-family-base);
}

.mobile-access-badge-content.is-on {
  background: #eef7f0;
  color: #32724b;
}

.mobile-access-badge:focus-visible .mobile-access-badge-content,
.mobile-access-badge:hover .mobile-access-badge-content { background: var(--noc-blue-05, #eff6ff); }

.supplier-phone-tooltip {
  z-index: 10000;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--noc-background, #fff);
  color: var(--text-color);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  font: 400 13px/18px var(--font-family-base);
  box-sizing: border-box;
  pointer-events: none;
}
.supplier-phone-tooltip.is-error {
  position: fixed;
  width: max-content;
  white-space: nowrap;
  color: var(--noc-red, #d9534f);
}
.supplier-phone-tooltip.is-error::before {
  content: '';
  position: absolute;
  top: -5px;
  left: var(--tooltip-arrow-left, 18px);
  width: 8px;
  height: 8px;
  border-top: 1px solid var(--border-color);
  border-left: 1px solid var(--border-color);
  background: var(--noc-background, #fff);
  transform: rotate(45deg);
}
.supplier-phone-tooltip.is-error.is-above::before {
  top: auto;
  bottom: -5px;
  transform: rotate(225deg);
}

</style>
